// Guided setup: stance & floor marks → camera placement (live framing check)
// → mark the tape ends → record the target stance (hands-free, 3 captures)
// → optional test pop-up → save.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { analyzeAttempt, type AttemptAnalysis } from '../../core/analysis';
import { buildFeedback } from '../../core/feedback';
import { observeFrame } from '../../core/features';
import { dist, type Vec2 } from '../../core/geometry';
import { boardHalfWidth, REFERENCE } from '../../core/board';
import { mappingFromTape, tapeOverlay, type PlaneMapping, type Stance, type TapeLayout } from '../../core/mapping';
import { paramsFor } from '../../core/params';
import type { PoseFrame } from '../../core/pose';
import { buildReference, onTapeMarks, type ReferenceResult } from '../../core/reference';
import { cameraMoved, checkAnchors, grayFrame, makeAnchors, personBox, type Box } from '../../engine/anchors';
import { speaker } from '../../engine/audio';
import { db, uid, type CalibrationRecord } from '../../engine/db';
import { canvasToBlob } from '../../engine/recorder';
import { framingCheck, StillnessDetector, VisibilityAudit } from '../../engine/setup';
import type { FrameSource, PoseSample } from '../../engine/source';
import { keepAwake } from '../../engine/wakelock';
import { tapeImagePoints } from '../../sim/world';
import { acquireSource, navigate, refreshModel, useApp, useRoute } from '../app-state';
import { BoardView } from '../components/BoardView';
import { Notice, OutcomeBadge, Ring, Segmented } from '../components/bits';
import { Icon } from '../components/Icon';
import { Stage } from '../components/Stage';
import { COLORS, drawMarker, drawOverlay } from '../draw/overlay';
import { useLiveFrames, usePortrait } from '../hooks';

type Step = 'basics' | 'camera' | 'tape' | 'stance' | 'check';

interface Draft {
  kind: 'camera' | 'demo';
  stance: Stance;
  useTape: boolean;
  stripLength: number;
  gap: number;
  stanceWidth: number;
}

interface Board {
  still: HTMLCanvasElement;
}

export function Setup() {
  const app = useApp();
  const route = useRoute();
  const prev = app.active;
  const recal = route.query.get('recal') === '1' && prev;
  const [draft, setDraft] = useState<Draft>(() => ({
    kind: route.query.get('demo') === '1' ? 'demo' : recal ? prev.source : 'camera',
    stance: recal ? prev.stance : 'regular',
    useTape: recal ? !!prev.tape : true,
    stripLength: prev?.tape?.stripLength ?? 70,
    gap: prev?.tape?.gap ?? REFERENCE.stanceWidth,
    stanceWidth: prev?.stanceWidth ?? REFERENCE.stanceWidth,
  }));
  const [step, setStep] = useState<Step>(recal ? 'camera' : 'basics');
  const [source, setSource] = useState<FrameSource | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [tape, setTape] = useState<{ layout: TapeLayout; mapping: PlaneMapping; warnings: string[] } | null>(null);
  const [ref, setRef] = useState<ReferenceResult | null>(null);
  const [stanceStill, setStanceStill] = useState<HTMLCanvasElement | null>(null);
  const [saving, setSaving] = useState(false);

  const steps: Step[] = draft.useTape ? ['basics', 'camera', 'tape', 'stance', 'check'] : ['basics', 'camera', 'stance', 'check'];
  const idx = steps.indexOf(step);

  useEffect(() => {
    keepAwake(true);
    return () => keepAwake(false);
  }, []);

  // Open the camera (or demo scene) once the basics are settled.
  useEffect(() => {
    if (step === 'basics') return;
    let cancelled = false;
    setSourceError(null);
    acquireSource(draft.kind, app.settings, draft.stance)
      .then(async (s) => {
        await refreshModel(s, app.settings);
        if (!cancelled) setSource(s);
      })
      .catch((e) => !cancelled && setSourceError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [step === 'basics', draft.kind, draft.stance, app.settings.model, app.settings.delegate, app.settings.cameraId]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (s: Step) => setStep(s);
  const next = () => setStep(steps[Math.min(steps.length - 1, idx + 1)]);
  const back = () => (idx <= 0 ? navigate('', { replace: true }) : setStep(steps[idx - 1]));

  const save = async (andPractice: boolean) => {
    if (!ref?.reference || !ref.mapping || !source) return;
    setSaving(true);
    try {
      let anchors: CalibrationRecord['anchors'] = [];
      let boardStillKey: string | null = null;
      if (tape && board) {
        anchors = boardAnchors(board, tape.layout);
        boardStillKey = await db.putBlob(await canvasToBlob(board.still));
      } else if (stanceStill) {
        const g = grayFrame(stanceStill, stanceStill.width, stanceStill.height);
        const box = personBox(lastStanceLm.current, 0.12);
        anchors = makeAnchors(g, [], box ? [box] : []);
      }
      const stanceStillKey = stanceStill ? await db.putBlob(await canvasToBlob(stanceStill)) : null;
      const record: CalibrationRecord = {
        id: uid(),
        createdAt: Date.now(),
        stance: draft.stance,
        source: draft.kind,
        camera: { deviceId: source.deviceId, label: source.label, width: source.width(), height: source.height() },
        mapping: ref.mapping,
        tape: tape?.layout ?? null,
        stanceWidth: draft.useTape ? null : draft.stanceWidth,
        reference: ref.reference,
        anchors,
        warnings: [...(tape?.warnings ?? []), ...ref.warnings],
        boardStillKey,
        stanceStillKey,
      };
      await db.putCalibration(record);
      app.updateSettings({ activeCalibrationId: record.id });
      await app.reloadCalibrations();
      app.toast('Setup saved');
      navigate(andPractice ? 'practice' : '', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const lastStanceLm = useRef<Float32Array | null>(null);

  return (
    <div className="wizard">
      <div className="wizard-head">
        <button className="icon-btn plain" onClick={back} aria-label="Back">
          <Icon name="back" />
        </button>
        <div className="steps" aria-label={`Step ${idx + 1} of ${steps.length}`}>
          {steps.map((s, i) => (
            <i key={s} className={i <= idx ? 'done' : ''} />
          ))}
        </div>
        <button className="icon-btn plain" onClick={() => navigate('', { replace: true })} aria-label="Close setup">
          <Icon name="x" />
        </button>
      </div>
      <div className="wizard-body">
        {step === 'basics' && <BasicsStep draft={draft} setDraft={setDraft} onNext={next} />}
        {step !== 'basics' && sourceError && (
          <div className="page stack">
            <Notice kind="error">{sourceError}</Notice>
            <div className="row">
              <button className="btn" onClick={() => setStep('basics')}>Back</button>
              <button className="btn primary" onClick={() => location.reload()}>Try again</button>
            </div>
          </div>
        )}
        {step !== 'basics' && !sourceError && !source && (
          <div className="page row" style={{ justifyContent: 'center', paddingTop: 80 }}>
            <div className="spinner" /> <span className="muted">{draft.kind === 'demo' ? 'Preparing the demo…' : 'Starting the camera and pose model…'}</span>
          </div>
        )}
        {source && step === 'camera' && <CameraStep source={source} draft={draft} onNext={next} />}
        {source && step === 'tape' && (
          <TapeStep
            source={source}
            draft={draft}
            board={board}
            setBoard={setBoard}
            tape={tape}
            setTape={setTape}
            onNext={next}
          />
        )}
        {source && step === 'stance' && (
          <StanceStep
            source={source}
            draft={draft}
            tape={tape}
            board={board}
            result={ref}
            setResult={setRef}
            onStill={(c, lm) => {
              setStanceStill(c);
              lastStanceLm.current = lm;
            }}
            onNext={next}
          />
        )}
        {source && step === 'check' && ref?.reference && ref.mapping && (
          <CheckStep source={source} draft={draft} result={ref} tape={tape} saving={saving} onSave={save} onRedo={() => go('stance')} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function BasicsStep({ draft, setDraft, onNext }: { draft: Draft; setDraft: (d: Draft) => void; onNext: () => void }) {
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  return (
    <div className="page stack lg" style={{ paddingBottom: 40 }}>
      <div className="stack sm">
        <h1>Set up your practice space</h1>
        <p className="muted">One phone, three pieces of tape, about five minutes.</p>
      </div>

      <div className="card stack">
        <h3>Your stance</h3>
        <Segmented
          block
          label="Stance"
          value={draft.stance}
          onChange={(stance) => set({ stance })}
          options={[
            { value: 'regular', label: 'Regular · left foot forward' },
            { value: 'goofy', label: 'Goofy · right foot forward' },
          ]}
        />
        <p className="small faint">Not sure? Most people lead with the foot they’d use to catch themselves if pushed from behind.</p>
      </div>

      <div className="card stack">
        <div className="row between">
          <h3>Floor marks</h3>
          <Segmented
            label="Floor marks"
            value={draft.useTape ? 'tape' : 'none'}
            onChange={(v) => set({ useTape: v === 'tape' })}
            options={[
              { value: 'tape', label: 'Tape' },
              { value: 'none', label: 'No tape' },
            ]}
          />
        </div>
        {draft.useTape ? (
          <>
            <TapeDiagram stance={draft.stance} />
            <ol className="small muted" style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
              <li>
                Lay the <b>back strip</b> where your back foot goes, roughly square across the board — on a real board that’s about 30 cm up from the tail, over the
                fins.
              </li>
              <li>
                Stand on it in your stance and lay the <b>front strip</b> under your front foot — about shoulder width or a little more ahead.
              </li>
              <li>
                Make the strips the same length, parallel and lined up end to end, with all four ends uncovered. Check it’s square: the two diagonals (end to opposite
                end) should match within about 2 cm.
              </li>
              <li>Optional: a strip along the middle, toward the nose, helps you line the strips up and lie down straight. The app doesn’t need it.</li>
            </ol>
            <div className="row wrap" style={{ alignItems: 'flex-end' }}>
              <label className="field grow">
                <span>Crosswise strip length</span>
                <div className="input-unit" data-unit="cm">
                  <input className="input num" inputMode="numeric" type="number" min={30} max={150} value={draft.stripLength} onChange={(e) => set({ stripLength: +e.target.value || 70 })} />
                </div>
              </label>
              <label className="field grow">
                <span>Gap between strips (centre to centre)</span>
                <div className="input-unit" data-unit="cm">
                  <input className="input num" inputMode="numeric" type="number" min={25} max={110} value={draft.gap} onChange={(e) => set({ gap: +e.target.value || 55 })} />
                </div>
              </label>
            </div>
            <p className="tiny faint">Measure these with a tape measure — it makes distances more accurate. The targets themselves come from the stance you record, not the tape.</p>
          </>
        ) : (
          <>
            <Notice>Without tape the app can’t correct for perspective, so distances are rougher and a moved camera can’t be detected as reliably. Tape is strongly recommended.</Notice>
            <label className="field">
              <span>Approximate stance width (between foot centres)</span>
              <div className="input-unit" data-unit="cm">
                <input className="input num" inputMode="numeric" type="number" min={25} max={110} value={draft.stanceWidth} onChange={(e) => set({ stanceWidth: +e.target.value || 55 })} />
              </div>
            </label>
          </>
        )}
      </div>

      <details className="card">
        <summary style={{ cursor: 'pointer', fontWeight: 620 }}>A sensible starting stance</summary>
        <ul className="small muted" style={{ margin: '10px 0 0', paddingLeft: 20, display: 'grid', gap: 6 }}>
          <li>
            <b>Back foot</b> near the tail, over the fins, roughly square (about 90°) across the board.
          </li>
          <li>
            <b>Front foot</b> about shoulder width or a little more ahead, around the middle of the board, angled about 30–45° toward the nose — not pointing straight
            at it.
          </li>
          <li>The arches of both feet over the stringer, not toward either rail.</li>
          <li>
            <b>Knees bent and angled in</b> toward each other; tuck the back knee in toward your front leg. Don’t let the knees splay apart.
          </li>
          <li>Bend at the knees, not the waist: back fairly straight, chest up and facing forward.</li>
          <li>One arm over each rail with elbows bent, and eyes where you’re going.</li>
        </ul>
        <p className="tiny faint" style={{ marginTop: 10 }}>
          Drawn from surf-coaching guides. The app measures how consistently you hit the stance you record — it doesn’t judge whether that stance is good technique, and
          the right width and angles vary with your body and board. A coach can tailor it to you.
        </p>
      </details>

      <button
        className="btn primary hero block"
        onClick={() => {
          speaker.unlock(); // the next steps talk to you while the screen faces away
          onNext();
        }}
      >
        Next: place the phone
      </button>
    </div>
  );
}

function TapeDiagram({ stance }: { stance: Stance }) {
  // A 6′ board drawn to scale, tail on the left (right for goofy riders).
  const flip = stance === 'goofy';
  const k = 272 / REFERENCE.boardLength;
  const X = (cm: number) => (flip ? 286 - cm * k : 14 + cm * k);
  const cy = 64;
  const back = REFERENCE.backFootFromTail;
  const frontAt = back + REFERENCE.stanceWidth;
  const outline = (() => {
    const pts: string[] = [];
    for (let i = 0; i <= 40; i++) pts.push(`${X((i / 40) * REFERENCE.boardLength)},${cy - boardHalfWidth(i / 40) * k}`);
    for (let i = 40; i >= 0; i--) pts.push(`${X((i / 40) * REFERENCE.boardLength)},${cy + boardHalfWidth(i / 40) * k}`);
    return `M${pts.join(' L')} Z`;
  })();
  const strip = 35 * k; // half of a 70 cm strip
  return (
    <svg viewBox="0 0 300 150" role="img" aria-label="Tape layout on a 6-foot board: a back strip over the fins near the tail, a front strip near the middle, and an optional centre line">
      <path d={outline} fill="var(--surface-2)" stroke="var(--hairline)" />
      {[[-15, REFERENCE.sideFinFromTail], [15, REFERENCE.sideFinFromTail], [0, REFERENCE.centreFinFromTail]].map(([dx, at]) => (
        <line key={`${dx}-${at}`} x1={X(at - 5)} y1={cy + dx * k} x2={X(at + 4)} y2={cy + dx * k} stroke="var(--text-3)" strokeWidth={3} strokeLinecap="round" />
      ))}
      {/* Optional centre line: drawn faint and dashed. */}
      <g opacity={0.45}>
        <line x1={X(2)} y1={cy} x2={X(REFERENCE.boardLength - 2)} y2={cy} stroke="var(--accent)" strokeWidth={3} strokeDasharray="6 5" />
        <path d={`M${X(REFERENCE.boardLength - 14)},${cy - 8} L${X(REFERENCE.boardLength - 2)},${cy} L${X(REFERENCE.boardLength - 14)},${cy + 8}`} fill="none" stroke="var(--accent)" strokeWidth={3} strokeLinejoin="round" />
      </g>
      <line x1={X(back)} y1={cy - strip} x2={X(back)} y2={cy + strip} stroke="var(--accent)" strokeWidth={6} />
      <line x1={X(frontAt)} y1={cy - strip} x2={X(frontAt)} y2={cy + strip} stroke="var(--accent)" strokeWidth={6} />
      {[cy - strip, cy + strip].map((y) => [back, frontAt].map((at) => <circle key={`${at}-${y}`} cx={X(at)} cy={y} r={4.5} fill="var(--text)" />))}
      <text x={X(back)} y={cy - strip - 7} textAnchor="middle" fontSize={10} fill="var(--text-2)">back foot</text>
      <text x={X(frontAt)} y={cy - strip - 7} textAnchor="middle" fontSize={10} fill="var(--text-2)">front foot</text>
      <text x={X(REFERENCE.boardLength - 4)} y={cy + 24} textAnchor={flip ? 'start' : 'end'} fontSize={10} fill="var(--text-2)">nose</text>
      <text x={X(2)} y={cy + 24} textAnchor={flip ? 'end' : 'start'} fontSize={10} fill="var(--text-2)">tail</text>
      <text x={X(back)} y={cy + strip + 14} textAnchor="middle" fontSize={9} fill="var(--text-3)">~30 cm from tail</text>
      <text x={X(frontAt)} y={cy + strip + 14} textAnchor="middle" fontSize={9} fill="var(--text-3)">~shoulder width ahead</text>
      <text x={150} y={146} textAnchor="middle" fontSize={9} fill="var(--text-3)">● = the four ends you’ll tap on screen</text>
    </svg>
  );
}

// ---------------------------------------------------------------------------

function CameraStep({ source, draft, onNext }: { source: FrameSource; draft: Draft; onNext: () => void }) {
  const portrait = usePortrait();
  const frontIsLeft = draft.stance === 'regular';
  const [okSince, setOkSince] = useState<number | null>(null);
  useEffect(() => {
    source.sim?.standInStance(performance.now());
  }, [source]);
  const { sample, latest } = useLiveFrames(source);
  const check = framingCheck(sample?.lm ?? null, frontIsLeft);
  useEffect(() => {
    if (check.ok) setOkSince((v) => v ?? performance.now());
    else setOkSince(null);
  }, [check.ok]);
  const stable = okSince !== null && performance.now() - okSince > 800;
  // The screen faces away from the rider, so the check is spoken, and once
  // passed it stays passed while they walk back to tap Next.
  const [verified, setVerified] = useState(false);
  const voice = useRef({ hint: null as string | null, since: 0, spoken: null as string | null, at: 0 });
  useEffect(() => {
    void speaker.say('Put the phone on its stand, then stand on the board in your stance. I’ll tell you what I can see.');
  }, []);
  useEffect(() => {
    if (!stable || verified) return;
    setVerified(true);
    speaker.tone('clean');
    void speaker.say('I can see all of you. This camera position works. Come back and tap Next.', { interrupt: true });
  }, [stable, verified]);
  useEffect(() => {
    if (verified) return;
    const v = voice.current;
    const now = performance.now();
    // Only talk to someone out at the board, not to the rider adjusting the phone up close.
    const hint = check.feetInFrame ? check.hint : null;
    if (hint !== v.hint) {
      v.hint = hint;
      v.since = now;
      return;
    }
    if (!hint || now - v.since < 1500) return;
    if (now - v.at < (hint === v.spoken ? 8000 : 3000)) return;
    v.spoken = hint;
    v.at = now;
    void speaker.say(hint, { interrupt: true });
  }, [sample, verified, check.hint, check.feetInFrame]);
  const stats = source.stats();
  const srcPortrait = source.height() > source.width();

  return (
    <div className="split">
      <Stage
        view={source.view}
        size={() => ({ w: source.width(), h: source.height() })}
        draw={(ctx, r, dpr) => drawOverlay(ctx, r, { lm: latest.current?.lm, skeleton: true, stance: draft.stance }, dpr)}
      />
      <div className="pane">
        <h2>Place your phone</h2>
        {srcPortrait ? (
          <Notice kind="error">
            The camera picture is portrait. Turn the phone sideways (landscape) — and if it stays portrait, turn off Portrait Orientation Lock in Control Centre.
            The measurements need the picture the right way up.
          </Notice>
        ) : (
          portrait && <Notice kind="warn">Turn the phone sideways (landscape) — the movement is wider than it is tall.</Notice>
        )}
        <ul className="small muted" style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 5 }}>
          <li>Rear camera, 1×, landscape, on a tripod or phone mount — you’ll tap it during setup, and a nudge spoils the measurements.</li>
          <li>About waist height (0.8–1.2 m), tilted down slightly, 2.5–3 m from the board. Closer than that cuts off your head or feet.</li>
          <li>On the side you <b>face</b> in your stance, angled diagonally so it sees your chest and toes.</li>
          <li>Board, tape ends and your whole body in frame — lying down and standing.</li>
        </ul>
        <div className="card stack sm">
          <div className="row between">
            <h3>Stand in your stance to check — I’ll say what I see</h3>
            <span className="tiny faint num">{stats.fps ? `${stats.fps.toFixed(0)} fps · ${stats.delegate}` : ''}</span>
          </div>
          <div className="checklist">
            <CheckItem ok={check.person} label="You’re in view" />
            <CheckItem ok={check.head} label="Head visible" />
            <CheckItem ok={check.hands} label="Hands visible" />
            <CheckItem ok={check.frontFoot} label="Front foot: heel and toes" />
            <CheckItem ok={check.rearFoot} label="Back foot: heel and toes" />
          </div>
          {check.hint && !verified && <p className="small" style={{ color: 'var(--warn)' }}>{check.hint}</p>}
          {(check.ok || verified) && <p className="small" style={{ color: 'var(--good)' }}>{verified ? 'Checked — this camera position works.' : 'Looks good from here.'}</p>}
        </div>
        <p className="tiny faint">Tip: the app judges the picture itself. If a foot keeps disappearing, raise the phone a little or move it further toward the nose or tail.</p>
        <button className="btn primary block" disabled={!(stable || verified) || srcPortrait} onClick={onNext}>
          {stable || verified ? 'Next' : 'Waiting for a clear view…'}
        </button>
        {!stable && !verified && !srcPortrait && (
          <button className="btn ghost block small" onClick={onNext}>
            Continue anyway
          </button>
        )}
      </div>
    </div>
  );
}

function CheckItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={`check ${ok ? 'ok' : 'no'}`}>
      <Icon name={ok ? 'checkCircle' : 'minus'} size={18} />
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------

const TAP_LABELS = ['back end 1', 'back end 2', 'front end 1', 'front end 2'];

function TapeStep({
  source,
  draft,
  board,
  setBoard,
  tape,
  setTape,
  onNext,
}: {
  source: FrameSource;
  draft: Draft;
  board: Board | null;
  setBoard: (b: Board | null) => void;
  tape: { layout: TapeLayout; mapping: PlaneMapping; warnings: string[] } | null;
  setTape: (t: { layout: TapeLayout; mapping: PlaneMapping; warnings: string[] } | null) => void;
  onNext: () => void;
}) {
  const [taps, setTaps] = useState<Vec2[]>(() => (tape ? [...tape.layout.rear, ...tape.layout.front] : []));
  const [problems, setProblems] = useState<string[]>([]);
  const drag = useRef<number | null>(null);
  const aspect = source.width() / source.height();
  const { sample } = useLiveFrames(board ? null : source);
  const nobody = !sample?.lm;

  useEffect(() => {
    if (!board) source.sim?.stepAway(performance.now());
  }, [board, source]);

  const capture = () => {
    setBoard({ still: source.snapshot(1600) });
    setTaps([]);
    setTape(null);
    setProblems([]);
  };

  // Recompute the floor mapping whenever four points are placed.
  useEffect(() => {
    if (taps.length < 4) {
      setTape(null);
      setProblems([]);
      return;
    }
    const layout: TapeLayout = { rear: [taps[0], taps[1]], front: [taps[2], taps[3]], stripLength: draft.stripLength, gap: draft.gap };
    const r = mappingFromTape(layout, aspect);
    setProblems(r.problems);
    setTape(r.mapping ? { layout, mapping: r.mapping, warnings: r.warnings } : null);
  }, [taps, draft.stripLength, draft.gap, aspect]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDrag = useCallback(
    (p: Vec2, phase: 'start' | 'move' | 'end') => {
      if (!board) return;
      if (phase === 'start') {
        let best = -1;
        let bestD = 0.05;
        taps.forEach((t, i) => {
          const d = Math.hypot((t.x - p.x) * aspect, t.y - p.y);
          if (d < bestD) {
            bestD = d;
            best = i;
          }
        });
        if (best >= 0) drag.current = best;
        else if (taps.length < 4) {
          drag.current = taps.length;
          setTaps([...taps, p]);
        }
      } else if (phase === 'move' && drag.current !== null) {
        const i = drag.current;
        setTaps((ts) => ts.map((t, j) => (j === i ? p : t)));
      } else if (phase === 'end') {
        drag.current = null;
      }
    },
    [board, taps, aspect],
  );

  const autoMark = () => {
    if (!source.sim) return;
    const t = tapeImagePoints(source.sim.world);
    setTaps([t.rear[0], t.rear[1], t.front[0], t.front[1]]);
  };

  const prompt =
    taps.length < 2
      ? 'Tap the two ends of the BACK strip (the one nearer the tail).'
      : taps.length < 4
        ? 'Now tap the two ends of the FRONT strip (nearer the nose).'
        : tape
          ? 'Check the two crosswise lines sit on your strips (the centre line is drawn for reference). Drag any point to adjust it.'
          : 'Those points don’t make an H. Drag them to the strip ends, or clear and start again.';

  return (
    <div className="split">
      <Stage
        view={board ? board.still : source.view}
        size={() => (board ? { w: board.still.width, h: board.still.height } : { w: source.width(), h: source.height() })}
        onDrag={board ? onDrag : undefined}
        draw={(ctx, r, dpr) => {
          if (!board) return;
          if (tape) {
            const o = tapeOverlay(tape.mapping, draft);
            ctx.save();
            ctx.lineCap = 'round';
            for (const [a, b] of [o.stringer, o.rear, o.front]) {
              ctx.strokeStyle = COLORS.halo;
              ctx.lineWidth = 6 * dpr;
              ctx.beginPath();
              ctx.moveTo(r.x + a.x * r.w, r.y + a.y * r.h);
              ctx.lineTo(r.x + b.x * r.w, r.y + b.y * r.h);
              ctx.stroke();
              ctx.strokeStyle = COLORS.tape;
              ctx.lineWidth = 2.5 * dpr;
              ctx.stroke();
            }
            ctx.restore();
          }
          taps.forEach((t, i) => drawMarker(ctx, { x: r.x + t.x * r.w, y: r.y + t.y * r.h }, String(i < 2 ? `B${i + 1}` : `F${i - 1}`), i < 2 ? COLORS.rear : COLORS.front, dpr));
          if (drag.current !== null && taps[drag.current]) drawLoupe(ctx, board.still, taps[drag.current], r, dpr);
        }}
      />
      <div className="pane">
        <h2>Mark the tape</h2>
        {!board ? (
          <>
            <p className="muted">Step off the board so all four ends of the crosswise strips are visible, then capture the picture.</p>
            {!nobody && <Notice>Someone is still in view — that’s fine if the four tape ends are uncovered.</Notice>}
            <button className="btn primary block" onClick={capture}>
              <Icon name="camera" /> Capture the board
            </button>
          </>
        ) : (
          <>
            <p className="muted" aria-live="polite">{prompt}</p>
            <div className="checklist">
              {TAP_LABELS.map((l, i) => (
                <div key={l} className={`check ${taps[i] ? 'ok' : 'no'}`}>
                  <Icon name={taps[i] ? 'checkCircle' : 'minus'} size={18} />
                  {l}
                </div>
              ))}
            </div>
            {problems.map((p) => (
              <Notice key={p} kind="error">{p}</Notice>
            ))}
            {tape?.warnings.map((w) => (
              <Notice key={w} kind="warn">{w}</Notice>
            ))}
            <div className="row">
              <button className="btn ghost small" onClick={() => setTaps(taps.slice(0, -1))} disabled={!taps.length}>
                Undo
              </button>
              <button className="btn ghost small" onClick={() => setTaps([])} disabled={!taps.length}>
                Clear
              </button>
              <button className="btn ghost small" onClick={() => setBoard(null)}>
                Retake
              </button>
            </div>
            {source.sim && taps.length < 4 && (
              <button className="btn small" onClick={autoMark}>
                <Icon name="sparkle" size={18} /> Auto-mark (demo)
              </button>
            )}
            <button className="btn primary block" disabled={!tape} onClick={onNext}>
              Lines match my tape
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** Magnifier drawn above the finger while placing a point. */
function drawLoupe(ctx: CanvasRenderingContext2D, img: HTMLCanvasElement, p: Vec2, r: { x: number; y: number; w: number; h: number }, dpr: number) {
  const R = 56 * dpr;
  const cx = r.x + p.x * r.w;
  const cy = Math.max(R + 8 * dpr, r.y + p.y * r.h - 96 * dpr);
  const zoom = 3;
  const srcR = (R / zoom) * (img.width / r.w);
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(img, p.x * img.width - srcR, p.y * img.height - srcR, srcR * 2, srcR * 2, cx - R, cy - R, R * 2, R * 2);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.5 * dpr;
  ctx.beginPath();
  ctx.moveTo(cx - 10 * dpr, cy);
  ctx.lineTo(cx + 10 * dpr, cy);
  ctx.moveTo(cx, cy - 10 * dpr);
  ctx.lineTo(cx, cy + 10 * dpr);
  ctx.stroke();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2 * dpr;
  ctx.stroke();
}

// ---------------------------------------------------------------------------

const CAPTURES = 3;

function StanceStep({
  source,
  draft,
  tape,
  board,
  result,
  setResult,
  onStill,
  onNext,
}: {
  source: FrameSource;
  draft: Draft;
  tape: { layout: TapeLayout; mapping: PlaneMapping } | null;
  board: Board | null;
  result: ReferenceResult | null;
  setResult: (r: ReferenceResult | null) => void;
  onStill: (c: HTMLCanvasElement, lm: Float32Array | null) => void;
  onNext: () => void;
}) {
  type Phase = 'idle' | 'hold' | 'stepOff' | 'review';
  const [phase, setPhase] = useState<Phase>(result?.reference ? 'review' : 'idle');
  const [captures, setCaptures] = useState<PoseFrame[][]>([]);
  const [progress, setProgress] = useState(0);
  const [swap, setSwap] = useState(false);
  const detector = useRef(new StillnessDetector(2000));
  const lastCapture = useRef<Float32Array | null>(null);
  const awaySince = useRef<number | null>(null);
  const offMarksSaidAt = useRef(-Infinity);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const capturesRef = useRef(captures);
  capturesRef.current = captures;

  const target = useMemo(
    () =>
      tape
        ? ({ kind: 'tape', mapping: tape.mapping, stripLength: draft.stripLength, gap: draft.gap } as const)
        : ({ kind: 'stance', stanceWidth: draft.stanceWidth, aspect: source.width() / source.height() } as const),
    [tape, draft, source],
  );

  const finish = useCallback(
    (all: PoseFrame[][], swapFeet: boolean, moved = false) => {
      const r = buildReference(all, target, draft.stance, { swapFeet });
      if (moved) r.warnings.unshift(CAMERA_MOVED);
      setResult(r);
      setPhase('review');
      if (r.reference) {
        speaker.tone(moved ? 'alert' : 'clean');
        void speaker.say(moved ? `Stance recorded. ${CAMERA_MOVED}` : 'Stance recorded.');
      } else {
        speaker.tone('unassessed');
        void speaker.say('I couldn’t record that. ' + (r.problems[0] ?? ''));
      }
    },
    [target, draft.stance, setResult],
  );

  const onFrame = useCallback(
    (s: PoseSample) => {
      const ph = phaseRef.current;
      if (ph === 'hold') {
        // Later captures must be back on the marks, not standing beside the board.
        const prev = lastCapture.current;
        if (prev && s.lm && feetMoved(prev, s.lm) > 0.08) {
          detector.current.reset();
          setProgress(0);
          return;
        }
        let frames = detector.current.push({ t: s.t, lm: s.lm });
        setProgress(detector.current.progress(s.t));
        // Holding still beside the board, or with feet together, isn't the stance.
        if (frames && target.kind === 'tape' && !onTapeMarks({ t: s.t, lm: s.lm }, target.mapping, target)) {
          frames = null;
          setProgress(0);
          if (s.t - offMarksSaidAt.current > 6000) {
            offMarksSaidAt.current = s.t;
            void speaker.say('Stand with one foot on each tape strip.', { interrupt: true });
          }
        }
        if (frames) {
          const all = [...capturesRef.current, frames];
          setCaptures(all);
          lastCapture.current = s.lm;
          if (all.length === 1) onStill(source.snapshot(1280), s.lm);
          if (all.length >= CAPTURES) {
            // The tape was marked on an earlier picture; a bump since then breaks the floor mapping.
            let moved = false;
            if (board && tape && s.lm) {
              const box = personBox(s.lm);
              moved = cameraMoved(checkAnchors(grayFrame(source.view, source.width(), source.height()), boardAnchors(board, tape.layout), box ? [box] : []));
            }
            finish(all, swap, moved);
          } else {
            speaker.tone('hold');
            void speaker.say(`Got it. Step off the board, then back into your stance. ${CAPTURES - all.length} to go.`);
            setPhase('stepOff');
            awaySince.current = null;
            if (source.sim) {
              const sim = source.sim;
              sim.stepAway(performance.now());
              setTimeout(() => sim.standInStance(performance.now()), 2600);
            }
          }
        }
      } else if (ph === 'stepOff') {
        // Wait until the rider has clearly (and for a moment) moved away from
        // the captured stance — a single dropped detection doesn't count.
        const prev = lastCapture.current;
        const moved = !s.lm || (prev && feetMoved(prev, s.lm) > 0.06);
        if (moved) awaySince.current ??= s.t;
        else awaySince.current = null;
        if (awaySince.current !== null && s.t - awaySince.current > 600) {
          detector.current.reset();
          setProgress(0);
          setPhase('hold');
        }
      }
    },
    [finish, onStill, source, swap, target, board, tape],
  );
  const { latest } = useLiveFrames(phase === 'review' ? null : source, onFrame);

  const start = () => {
    speaker.unlock();
    lastCapture.current = null;
    setCaptures([]);
    setResult(null);
    detector.current.reset();
    setProgress(0);
    setPhase('hold');
    void speaker.say('Stand in your stance on the marks and hold still.');
    source.sim?.standInStance(performance.now());
  };

  const ref = result?.reference;
  const mappingForView = result?.mapping ?? tape?.mapping ?? null;

  return (
    <div className="split">
      <Stage
        view={source.view}
        size={() => ({ w: source.width(), h: source.height() })}
        draw={(ctx, r, dpr) =>
          drawOverlay(
            ctx,
            r,
            {
              mapping: mappingForView,
              tape: tape ? draft : null,
              reference: ref ?? null,
              tol: ref ? { along: 8, across: 8 } : null,
              lm: latest.current?.lm,
              stance: draft.stance,
              skeleton: false,
            },
            dpr,
          )
        }
      />
      <div className="pane">
        <h2>Record your stance</h2>
        {phase === 'idle' && (
          <>
            <p className="muted">
              Walk to the board and stand in the stance you want to land in, feet on the marks. Hold still for two seconds. I’ll capture it {CAPTURES} times — step off and
              back on in between, so the target reflects where you naturally stand.
            </p>
            <p className="small faint">Everything is spoken, so you don’t need to look at the screen.</p>
            <button className="btn primary block hero" onClick={start}>
              <Icon name="play" /> Start
            </button>
          </>
        )}
        {(phase === 'hold' || phase === 'stepOff') && (
          <div className="row" style={{ gap: 18 }}>
            <Ring value={phase === 'hold' ? progress : 1} label={`${captures.length}/${CAPTURES}`} />
            <div className="stack sm">
              <h3>{phase === 'hold' ? 'Hold your stance…' : 'Step off, then back on'}</h3>
              <p className="small muted">{phase === 'hold' ? 'Both feet must be visible and still.' : 'Waiting for you to move away.'}</p>
            </div>
          </div>
        )}
        {phase === 'review' && result && (
          <>
            {result.problems.map((p) => (
              <Notice key={p} kind="error">{p}</Notice>
            ))}
            {ref && (
              <>
                <BoardView reference={ref} stance={draft.stance} tol={{ along: 8, across: 8 }} tape={tape ? draft : null} captures={ref.captures} />
                <div className="tiles">
                  <div className="tile">
                    <div className="label">Tracking jitter</div>
                    <div className="value num">±{ref.noise.toFixed(1)} cm</div>
                    <div className="sub">while standing still</div>
                  </div>
                  <div className="tile">
                    <div className="label">Stance repeatability</div>
                    <div className="value num">{ref.captures.length > 1 ? `±${Math.max(ref.repeatability.front, ref.repeatability.rear).toFixed(1)} cm` : '—'}</div>
                    <div className="sub">across captures</div>
                  </div>
                </div>
              </>
            )}
            {result.warnings.map((w) => (
              <Notice key={w} kind="warn">{w}</Notice>
            ))}
            {!tape && ref && (
              <button
                className="btn ghost small"
                onClick={() => {
                  setSwap(!swap);
                  finish(captures, !swap);
                }}
              >
                <Icon name="swap" size={18} /> Swap front and back foot
              </button>
            )}
            <div className="row">
              <button className="btn ghost grow" onClick={start}>
                Record again
              </button>
              <button className="btn primary grow" disabled={!ref} onClick={onNext}>
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CAMERA_MOVED = 'The camera seems to have moved since you photographed the board. If you bumped it, go back and retake the board picture.';

/** Patches of the tape ends and textured scenery in the board picture, for spotting a moved camera. */
function boardAnchors(board: Board, layout: TapeLayout) {
  const g = grayFrame(board.still, board.still.width, board.still.height);
  const pts = [...layout.rear, ...layout.front];
  const pad = 0.12;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const exclude: Box = { x0: Math.min(...xs) - pad, x1: Math.max(...xs) + pad, y0: Math.min(...ys) - 0.45, y1: Math.max(...ys) + pad };
  return makeAnchors(g, pts, [exclude]);
}

function feetMoved(a: Float32Array, b: Float32Array): number {
  let m = 0;
  for (const i of [29, 30, 31, 32]) m = Math.max(m, dist({ x: a[i * 3], y: a[i * 3 + 1] }, { x: b[i * 3], y: b[i * 3 + 1] }));
  return m;
}

// ---------------------------------------------------------------------------

function CheckStep({
  source,
  draft,
  result,
  tape,
  saving,
  onSave,
  onRedo,
}: {
  source: FrameSource;
  draft: Draft;
  result: ReferenceResult;
  tape: { layout: TapeLayout; mapping: PlaneMapping } | null;
  saving: boolean;
  onSave: (andPractice: boolean) => void;
  onRedo: () => void;
}) {
  type Phase = 'idle' | 'lieDown' | 'armed' | 'capture' | 'done';
  const app = useApp();
  const [phase, setPhase] = useState<Phase>('idle');
  const [analysis, setAnalysis] = useState<AttemptAnalysis | null>(null);
  const [audit, setAudit] = useState<{ front: number; rear: number } | null>(null);
  const ref = result.reference!;
  const mapping = result.mapping!;
  const params = useMemo(() => paramsFor({ strictness: app.settings.strictness, holdMs: app.settings.holdMs }, ref.noise), [app.settings, ref.noise]);
  const st = useRef({ phase: 'idle' as Phase, proneSince: null as number | null, since: 0, frames: [] as PoseFrame[], t0: 0, cue: 0, audit: new VisibilityAudit(draft.stance === 'regular'), timer: 0 as unknown as ReturnType<typeof setTimeout> });

  const set = (p: Phase) => {
    st.current.phase = p;
    setPhase(p);
  };

  const onFrame = useCallback(
    (s: PoseSample) => {
      const S = st.current;
      if (S.phase === 'lieDown') {
        const o = s.lm ? observeFrame({ t: s.t, lm: s.lm }, mapping, { minVisibility: 0.3 }) : null;
        const prone = o?.height != null && o.height / ref.standHeight < 0.45;
        if (prone) S.proneSince ??= s.t;
        else S.proneSince = null;
        if ((S.proneSince !== null && s.t - S.proneSince > 1000) || s.t - S.since > 15000) {
          set('armed');
          S.frames = [];
          S.t0 = s.t;
          speaker.tone('ready');
          S.timer = setTimeout(() => {
            speaker.tone('go');
            S.cue = performance.now() - S.t0;
            set('capture');
            source.sim?.popUp(performance.now());
          }, 1500);
        }
      } else if (S.phase === 'armed' || S.phase === 'capture') {
        S.frames.push({ t: s.t - S.t0, lm: s.lm });
        if (S.phase !== 'capture') return;
        const a = analyzeAttempt(S.frames, { mapping, reference: ref, params, stance: draft.stance, cueTime: S.cue });
        if (a.events.onset !== undefined || a.events.arrival !== undefined) S.audit.push(s.lm);
        if (a.holdComplete || s.t - S.t0 - S.cue > 9000) {
          speaker.tone('hold');
          setAnalysis(a);
          const rep = S.audit.report();
          setAudit({ front: rep.front, rear: rep.rear });
          const fb = buildFeedback(a, params);
          void speaker.say(fb.spoken);
          void speaker.say(
            rep.front < 0.8 || rep.rear < 0.8
              ? `Your ${rep.front < 0.8 && rep.rear < 0.8 ? 'feet were' : rep.front < 0.8 ? 'front foot was' : 'back foot was'} hidden for part of the pop-up. Try raising the phone, or moving it toward the nose or tail.`
              : 'Both feet stayed visible. This camera position works.',
          );
          set('done');
        }
      }
    },
    [mapping, ref, params, draft.stance, source],
  );
  const { latest } = useLiveFrames(phase === 'idle' || phase === 'done' ? null : source, onFrame);
  useEffect(() => () => clearTimeout(st.current.timer), []);

  const start = () => {
    speaker.unlock();
    setAnalysis(null);
    setAudit(null);
    st.current.audit = new VisibilityAudit(draft.stance === 'regular');
    st.current.proneSince = null;
    st.current.since = performance.now();
    set('lieDown');
    void speaker.say('Lie down on the board. After the beep, do a slow pop-up and hold your landing.');
    source.sim?.lieDown(performance.now());
  };

  const weak = audit && (audit.front < 0.8 || audit.rear < 0.8);
  return (
    <div className="split">
      <Stage
        view={source.view}
        size={() => ({ w: source.width(), h: source.height() })}
        draw={(ctx, r, dpr) =>
          drawOverlay(ctx, r, { mapping, tape: tape ? draft : null, reference: ref, tol: { along: params.tolAlong, across: params.tolAcross }, lm: latest.current?.lm, stance: draft.stance }, dpr)
        }
      />
      <div className="pane">
        <h2>Test pop-up</h2>
        {phase === 'idle' && (
          <>
            <p className="muted">One slow practice pop-up checks the camera can see both feet through the whole movement — the hardest part to measure. It isn’t saved.</p>
            <button className="btn primary block" onClick={start}>
              <Icon name="play" /> Try a test pop-up
            </button>
            <button className="btn ghost block" disabled={saving} onClick={() => onSave(false)}>
              Skip and save setup
            </button>
          </>
        )}
        {(phase === 'lieDown' || phase === 'armed' || phase === 'capture') && (
          <div className="row">
            <div className="spinner" />
            <p className="muted">{phase === 'lieDown' ? 'Lie down on the board…' : phase === 'armed' ? 'Wait for the beep…' : 'Pop up slowly and hold…'}</p>
          </div>
        )}
        {phase === 'done' && analysis && audit && (
          <>
            <OutcomeBadge outcome={analysis.outcome} large />
            {analysis.outcome !== 'clean' && <p className="muted">{buildFeedback(analysis, params).spoken}</p>}
            <div className="tiles">
              <div className="tile">
                <div className="label">Front foot visible</div>
                <div className="value num">{Math.round(audit.front * 100)}%</div>
                <div className="sub">after you started moving</div>
              </div>
              <div className="tile">
                <div className="label">Back foot visible</div>
                <div className="value num">{Math.round(audit.rear * 100)}%</div>
                <div className="sub">after you started moving</div>
              </div>
            </div>
            {weak ? (
              <Notice kind="warn">
                A foot was hidden for part of the movement. Try raising the phone a little, or moving it further toward the nose or tail, then record your stance again.
              </Notice>
            ) : (
              <Notice kind="good">Both feet stayed visible. This camera position works.</Notice>
            )}
            <div className="row">
              <button className="btn ghost grow" onClick={start}>
                Again
              </button>
              {weak && (
                <button className="btn ghost grow" onClick={onRedo}>
                  Re-record stance
                </button>
              )}
            </div>
            <button className="btn primary block hero" disabled={saving} onClick={() => onSave(true)}>
              Save and start practising
            </button>
            <button className="btn ghost block small" disabled={saving} onClick={() => onSave(false)}>
              Save setup
            </button>
          </>
        )}
      </div>
    </div>
  );
}

